/**
 * Token Management Utility
 *
 * Provides centralized token management for both localStorage and React state.
 * Ensures consistency between localStorage operations and component state.
 */

import { useState, useEffect, useCallback } from 'react';

const AUTH_TOKEN_KEY = 'auth-token';
const AUTH_DATA_KEY = 'authData';

// Global variable to store URL token during app initialization
let urlToken: string | null = null;

export interface TokenManager {
  getToken: () => string | null;
  setToken: (token: string) => void;
  removeToken: () => void;
  hasToken: () => boolean;
}

export interface AuthData {
  user: BeatlyApi.UserObject;
  token: string;
}

export interface AuthDataManager {
  getAuthData: () => AuthData | null;
  setAuthData: (authData: AuthData) => void;
  removeAuthData: () => void;
  hasAuthData: () => boolean;
}

/**
 * Initialize URL token parsing - must be called before any other operations
 * This ensures URL token takes priority over localStorage token
 */
export function initializeUrlToken(): string | null {
  console.log('🔍 [initializeUrlToken] Starting URL token initialization');

  if (urlToken !== null) {
    console.log('🔍 [initializeUrlToken] URL token already parsed:', urlToken.substring(0, 50) + '...');
    return urlToken; // Already parsed
  }

  const urlParams = new URLSearchParams(window.location.search);
  const jwtToken = urlParams.get('jwt');

  console.log('🔍 [initializeUrlToken] URL search params:', window.location.search);
  console.log('🔍 [initializeUrlToken] JWT token from URL:', jwtToken ? jwtToken.substring(0, 50) + '...' : 'null');

  if (jwtToken) {
    urlToken = jwtToken;
    console.log('🔍 [initializeUrlToken] Setting urlToken global variable:', urlToken.substring(0, 50) + '...');
    // Immediately set in localStorage to prevent race conditions
    localStorage.setItem(AUTH_TOKEN_KEY, jwtToken);
    console.log('🔍 [initializeUrlToken] Set token in localStorage:', jwtToken.substring(0, 50) + '...');
  } else {
    console.log('🔍 [initializeUrlToken] No JWT token found in URL');
  }

  console.log('🔍 [initializeUrlToken] Returning urlToken:', urlToken ? urlToken.substring(0, 50) + '...' : 'null');
  return urlToken;
}

/**
 * Clear URL token after authentication is complete
 */
export function clearUrlToken(): void {
  console.log('🔍 [clearUrlToken] Clearing URL token, was:', urlToken ? urlToken.substring(0, 50) + '...' : 'null');
  urlToken = null;
  console.log('🔍 [clearUrlToken] URL token cleared');
}

/**
 * Token Manager - handles localStorage operations for auth tokens
 */
export const tokenManager: TokenManager = {
  getToken: (): string | null => {
    console.log('🔍 [tokenManager.getToken] Called');
    console.log('🔍 [tokenManager.getToken] urlToken:', urlToken ? urlToken.substring(0, 50) + '...' : 'null');

    // Prioritize URL token if available
    if (urlToken) {
      console.log('🔍 [tokenManager.getToken] Returning URL token:', urlToken.substring(0, 50) + '...');
      return urlToken;
    }

    const localStorageToken = localStorage.getItem(AUTH_TOKEN_KEY);
    console.log('🔍 [tokenManager.getToken] localStorage token:', localStorageToken ? localStorageToken.substring(0, 50) + '...' : 'null');
    console.log('🔍 [tokenManager.getToken] Returning localStorage token:', localStorageToken ? localStorageToken.substring(0, 50) + '...' : 'null');
    return localStorageToken;
  },

  setToken: (token: string): void => {
    console.log('🔍 [tokenManager.setToken] Setting token:', token.substring(0, 50) + '...');
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    // Clear URL token when setting a new token
    console.log('🔍 [tokenManager.setToken] Clearing URL token, was:', urlToken ? urlToken.substring(0, 50) + '...' : 'null');
    urlToken = null;
    console.log('🔍 [tokenManager.setToken] Token set and URL token cleared');
  },

  removeToken: (): void => {
    console.log('🔍 [tokenManager.removeToken] Removing token from localStorage');
    localStorage.removeItem(AUTH_TOKEN_KEY);
    console.log('🔍 [tokenManager.removeToken] Clearing URL token, was:', urlToken ? urlToken.substring(0, 50) + '...' : 'null');
    urlToken = null;
    console.log('🔍 [tokenManager.removeToken] Token removed and URL token cleared');
  },

  hasToken: (): boolean => {
    const hasUrlToken = !!urlToken;
    const hasLocalStorageToken = !!localStorage.getItem(AUTH_TOKEN_KEY);
    const hasAnyToken = hasUrlToken || hasLocalStorageToken;

    console.log('🔍 [tokenManager.hasToken] urlToken exists:', hasUrlToken);
    console.log('🔍 [tokenManager.hasToken] localStorage token exists:', hasLocalStorageToken);
    console.log('🔍 [tokenManager.hasToken] has any token:', hasAnyToken);

    return hasAnyToken;
  },
};

/**
 * Auth Data Manager - handles localStorage operations for auth data
 */
export const authDataManager: AuthDataManager = {
  getAuthData: (): AuthData | null => {
    try {
      const stored = localStorage.getItem(AUTH_DATA_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  setAuthData: (authData: AuthData): void => {
    localStorage.setItem(AUTH_DATA_KEY, JSON.stringify(authData));
  },

  removeAuthData: (): void => {
    localStorage.removeItem(AUTH_DATA_KEY);
  },

  hasAuthData: (): boolean => {
    return !!localStorage.getItem(AUTH_DATA_KEY);
  },
};

/**
 * Combined Token Operations
 *
 * These functions handle both token and auth data together for consistency
 */

/**
 * Store authentication data (both token and user data)
 */
export function storeAuthData(authData: AuthData): void {
  tokenManager.setToken(authData.token);
  authDataManager.setAuthData(authData);
}

/**
 * Clear all authentication data
 */
export function clearAuthData(): void {
  tokenManager.removeToken();
  authDataManager.removeAuthData();
}

/**
 * Get current authentication state
 */
export function getCurrentAuthState(): {
  token: string | null;
  authData: AuthData | null;
  isAuthenticated: boolean;
} {
  const token = tokenManager.getToken();
  const authData = authDataManager.getAuthData();

  return {
    token,
    authData,
    isAuthenticated: !!(token && authData?.user?.is_client),
  };
}

/**
 * React Hook for Token Management
 *
 * Provides a hook that keeps React state in sync with localStorage
 */
export function useTokenManager() {
  const [token, setTokenState] = useState<string | null>(null);
  const [authData, setAuthDataState] = useState<AuthData | null>(null);

  // Initialize state from localStorage
  useEffect(() => {
    setTokenState(tokenManager.getToken());
    setAuthDataState(authDataManager.getAuthData());
  }, []);

  // Synchronized token setter
  const setToken = useCallback((newToken: string) => {
    tokenManager.setToken(newToken);
    setTokenState(newToken);
  }, []);

  // Synchronized token remover
  const removeToken = useCallback(() => {
    tokenManager.removeToken();
    setTokenState(null);
  }, []);

  // Synchronized auth data setter
  const setAuthData = useCallback((newAuthData: AuthData) => {
    storeAuthData(newAuthData);
    setTokenState(newAuthData.token);
    setAuthDataState(newAuthData);
  }, []);

  // Synchronized auth data remover
  const removeAuthData = useCallback(() => {
    clearAuthData();
    setTokenState(null);
    setAuthDataState(null);
  }, []);

  return {
    token,
    authData,
    setToken,
    removeToken,
    setAuthData,
    removeAuthData,
    isAuthenticated: !!(token && authData?.user?.is_client),
  };
}

/**
 * Legacy compatibility - maintains existing getAuthToken function
 * @deprecated Use tokenManager.getToken() instead
 */
export const getAuthToken = tokenManager.getToken;
